import { Bar } from '@reactchartjs/react-chart.js';
import { Alert, Button, Card, DatePicker, Flex, Spin, Typography } from '@duxton-web/ui';
import dayjs from 'dayjs';
import i18n from 'i18n-js';
import React from 'react';
import { get } from 'lodash';

import {
  buildJourneyTimelineChart,
  getJourneyTimelineRangeError,
  getJourneyTimelineDisplayRange,
  resolveJourneyTimelineDisplayTimezone,
  JourneyCommsTimelineResponse,
  MAX_JOURNEY_TIMELINE_DAYS,
} from './JourneyCommsTimeline.utils';
import './JourneyCampaignDetailSections.scss';

type Props = {
  groupIDs: string[];
  displayTimezone?: string;
  startDate: string;
  endDate: string;
  onRangeChange: (start: string, end: string) => void;
  onPreview: () => void;
  timeline?: JourneyCommsTimelineResponse;
  previewRange?: { startDate: string; endDate: string };
  isPending?: boolean;
  isFailure?: boolean;
};

const getChartOptions = (displayTimezone: string) => ({
  maintainAspectRatio: false,
  responsive: true,
  legend: { display: false },
  plugins: { datalabels: { display: false } },
  scales: {
    xAxes: [{ stacked: true,
      gridLines: { display: false },
      scaleLabel: { display: true, labelString: i18n.t('journey_campaign_comms_timeline.axis_label', { timezone: displayTimezone, defaultValue: 'Date and time ({{timezone}})' }) },
      ticks: { autoSkip: true, maxTicksLimit: 12, maxRotation: 0 } }],
    yAxes: [{ stacked: true,
      gridLines: { color: '#eef1f4', drawBorder: false },
      scaleLabel: { display: true, labelString: 'Number of comms' },
      ticks: { beginAtZero: true, precision: 0, callback: (value) => Number(value).toLocaleString('en-US') } }],
  },
  tooltips: {
    mode: 'index',
    intersect: false,
    filter: (item) => Number(item.yLabel) > 0,
    callbacks: { label: (item, data) => `${data.datasets[item.datasetIndex].label}: ${Number(item.yLabel).toLocaleString('en-US')} comms` },
  },
});

export const JourneyCommsTimeline = ({
  groupIDs, displayTimezone = 'UTC', startDate, endDate, onRangeChange, onPreview, timeline, previewRange, isPending, isFailure,
}: Props) => {
  const timezone = resolveJourneyTimelineDisplayTimezone([], displayTimezone);
  const chartOptions = getChartOptions(timezone);
  const rangeError = getJourneyTimelineRangeError(startDate, endDate);
  const chart = timeline ? buildJourneyTimelineChart(timeline, groupIDs, timezone) : undefined;
  const displayRange = getJourneyTimelineDisplayRange(
    get(timeline, 'startDate') || get(previewRange, 'startDate') || startDate,
    get(timeline, 'endDate') || get(previewRange, 'endDate') || endDate,
    timezone,
  );
  const changeStart = (date) => {
    if (!date || !date.isValid()) {
      onRangeChange('', endDate);
      return;
    }
    // A picker selects a calendar date, not an instant to convert from local time.
    const start = dayjs.utc(date.format('YYYY-MM-DD'));
    const end = dayjs.utc(endDate);
    const nextEnd = !end.isValid() || end.isBefore(start, 'day') || end.diff(start, 'day') >= MAX_JOURNEY_TIMELINE_DAYS
      ? start.add(MAX_JOURNEY_TIMELINE_DAYS - 1, 'day') : end;
    const nextStartValue = start.format('YYYY-MM-DD');
    const nextEndValue = nextEnd.format('YYYY-MM-DD');
    if (!getJourneyTimelineRangeError(nextStartValue, nextEndValue)) onRangeChange(nextStartValue, nextEndValue);
  };
  const changeEnd = (date) => {
    if (!date || !date.isValid()) {
      onRangeChange(startDate, '');
      return;
    }
    const end = dayjs.utc(date.format('YYYY-MM-DD'));
    const start = dayjs.utc(startDate);
    const nextStart = !start.isValid() || end.isBefore(start, 'day') || end.diff(start, 'day') >= MAX_JOURNEY_TIMELINE_DAYS
      ? end.subtract(MAX_JOURNEY_TIMELINE_DAYS - 1, 'day') : start;
    const nextStartValue = nextStart.format('YYYY-MM-DD');
    const nextEndValue = end.format('YYYY-MM-DD');
    if (!getJourneyTimelineRangeError(nextStartValue, nextEndValue)) onRangeChange(nextStartValue, nextEndValue);
  };
  return (
    <Card className="journey-comms-timeline" title="Preview Comms Timeline">
      <Flex vertical gap="16px">
        <div className="journey-comms-timeline-filters">
          <Flex vertical gap="6px">
            <label htmlFor="journey-timeline-start"><Typography.Text strong>{i18n.t('journey_campaign_comms_timeline.start_date', { defaultValue: 'Query start date (UTC)' })}</Typography.Text></label>
            <DatePicker
              id="journey-timeline-start" aria-label={i18n.t('journey_campaign_comms_timeline.start_date_accessible', { defaultValue: 'Timeline start date (UTC)' })} aria-describedby="journey-timeline-utc-hint" allowClear={false}
              value={startDate ? dayjs(startDate) : null} onChange={changeStart} style={{ width: '100%' }}
            />
          </Flex>
          <Flex vertical gap="6px">
            <label htmlFor="journey-timeline-end"><Typography.Text strong>{i18n.t('journey_campaign_comms_timeline.end_date', { defaultValue: 'Query end date (UTC)' })}</Typography.Text></label>
            <DatePicker
              id="journey-timeline-end" aria-label={i18n.t('journey_campaign_comms_timeline.end_date_accessible', { defaultValue: 'Timeline end date (UTC)' })} aria-describedby="journey-timeline-utc-hint" allowClear={false}
              value={endDate ? dayjs(endDate) : null} onChange={changeEnd} style={{ width: '100%' }}
            />
          </Flex>
          <Typography.Text type="secondary">Maximum range: {MAX_JOURNEY_TIMELINE_DAYS} days</Typography.Text>
          <Button
            disabled={!groupIDs.length || Boolean(rangeError) || isPending} loading={isPending}
            onClick={onPreview} type="primary"
          >Preview</Button>
        </div>
        <Typography.Text id="journey-timeline-utc-hint" type="secondary">
          {i18n.t('journey_campaign_comms_timeline.utc_hint', {
            timezone,
            defaultValue: 'Query dates cover full UTC days. Chart times are shown in {{timezone}}, not your browser timezone.',
          })}
        </Typography.Text>
        {rangeError && <Alert message={rangeError} showIcon type="warning" />}
        {!groupIDs.length && <Typography.Text type="secondary">No group IDs are available for this country.</Typography.Text>}
        {isPending && (
          <div className="journey-comms-timeline-placeholder" role="status"><Spin /><Typography.Text>Loading comms timeline...</Typography.Text></div>
        )}
        {!isPending && isFailure && <Alert message="Unable to load the comms timeline. Please try Preview again." showIcon type="error" />}
        {!isPending && !isFailure && !chart && (
          <div className="journey-comms-timeline-placeholder">
            <Typography.Text strong>Comms preview is ready when you are</Typography.Text>
            <Typography.Text type="secondary">Select a date range, then click Preview to load the timeline.</Typography.Text>
          </div>
        )}
        {!isPending && !isFailure && chart && (
          <React.Fragment>
            <Typography.Text type="secondary">
              {i18n.t('journey_campaign_comms_timeline.display_range', {
                start: displayRange.start,
                end: displayRange.end,
                timezone,
                defaultValue: 'Comms by group ID from {{start}} to {{end}} ({{timezone}}).',
              })}
            </Typography.Text>
            <div className="journey-comms-timeline-summary">
              <Flex align="center" justify="space-between" className="journey-comms-timeline-summary-header">
                <Typography.Text strong>Group ID totals</Typography.Text>
                <Typography.Text type="secondary">{chart.totals.length} {chart.totals.length === 1 ? 'group' : 'groups'}</Typography.Text>
              </Flex>
              <div className="journey-comms-timeline-totals">
                {chart.totals.map((item) => (
                  <div className="journey-comms-timeline-total" key={item.groupID} title={item.groupID}>
                    <span aria-hidden="true" className="journey-comms-timeline-color" style={{ backgroundColor: item.color }} />
                    <div className="journey-comms-timeline-total-content">
                      <Typography.Text className="journey-comms-timeline-group-id">{item.groupID}</Typography.Text>
                      <Typography.Text strong>{item.total.toLocaleString('en-US')} comms</Typography.Text>
                      {item.isRangeSum && <Typography.Text type="secondary">Sum over selected range</Typography.Text>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {chart.datasets.some((dataset) => dataset.data.some((count) => count > 0)) ? (
              <div className="journey-comms-timeline-chart" role="img" aria-label={i18n.t('journey_campaign_comms_timeline.chart_label', { timezone, defaultValue: 'Comms by group, date and time ({{timezone}})' })}>
                <Bar data={{ labels: chart.labels, datasets: chart.datasets }} options={chartOptions} />
              </div>
            ) : <div className="journey-comms-timeline-placeholder"><Typography.Text>No comms were found for this date range.</Typography.Text></div>}
          </React.Fragment>
        )}
      </Flex>
    </Card>
  );
};
